// Port of new arty's components/arty/community/CommunityAskDialog.tsx (arty repo).
// Client divergences: the shell is @artstorefronts/ui's Dialog, copy is `arty-card`
// `community-ask.*`, the communities come from the sidebar's `MY_COMMUNITIES_QUERY` (so
// the list is usually already cached), and Escape is left to Radix's layer stack instead of
// a capture-phase document listener - see the comment on `DialogContent` below. The header
// also shows the piece's thumbnail, which new arty does not, and a post that landed calls
// `onPosted` (the card leaves the flow) while every other exit calls `onClose` (the card
// returns to More when the ask was opened from there).
import { useState, type ComponentType, type RefObject } from 'react';
import { useQuery } from '@apollo/client';
import { ArrowLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@artstorefronts/ui';

import {
  CritiqueAskForm,
  PollAskForm,
  PricingAskForm,
  RatingAskForm,
  type AskFormProps,
  type InitialArtwork,
} from '~/components/arty/community/AskForms';
import { CommunityPicker } from '~/components/arty/community/CommunityPicker';
import { useAskSubmit } from '~/components/arty/community/useAskSubmit';
import { MY_COMMUNITIES_QUERY } from '~/graphql/queries';
import type { CommunityKind } from '~/lib/arty-actions/catalog';

const FORMS: Record<CommunityKind, ComponentType<AskFormProps>> = {
  critique: CritiqueAskForm,
  rating: RatingAskForm,
  pricing: PricingAskForm,
  poll: PollAskForm,
};

/** The piece being asked about, at the top of the form it seeds. */
function PieceRow({ artwork }: { artwork: InitialArtwork }) {
  const { t } = useTranslation('arty-card');
  const meta = [artwork.medium, artwork.dimensions].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-2" data-ask-piece="">
      <div className="size-12 shrink-0 overflow-hidden rounded-md bg-muted">
        {artwork.thumb ? <img alt="" className="size-full object-cover" src={artwork.thumb} /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">{artwork.title || t('sheet.untitled')}</p>
        {meta ? <p className="truncate text-xs text-muted-foreground">{meta}</p> : null}
      </div>
    </div>
  );
}

const ICON_BUTTON =
  'flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted disabled:opacity-50';

/**
 * One shell for the whole ask, in two steps: pick a community (skipped when the
 * artist is in exactly one), then that ask's submission form, seeded with the
 * piece the card was opened on.
 *
 * Both steps share a dialog so there is one set of focus, scroll-lock and dismiss
 * rules. They differ on one: the backdrop leaves the picker (nothing is written yet)
 * but never a half-filled form. A post in flight cannot be dismissed at all, since
 * reopening mid-submit is how one ask gets posted twice.
 */
export function CommunityAskDialog({
  kind,
  imageId,
  initialArtwork = {},
  open,
  onClose,
  onPosted,
  returnFocusTo,
}: {
  kind: CommunityKind;
  imageId: string;
  /** What the card knows about the piece: the header thumbnail, the piece row, the form's seeds. */
  initialArtwork?: InitialArtwork;
  open: boolean;
  onClose: () => void;
  /** After a post lands; defaults to `onClose`. */
  onPosted?: () => void;
  /** The control that opened this; focus goes back to it on every exit. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation('arty-card');
  const [picked, setPicked] = useState<any>(null);
  const { data, loading } = useQuery(MY_COMMUNITIES_QUERY, { skip: !open });

  const communities: any[] = (data?.myCommunities ?? []).filter(Boolean);
  // One community needs no picker: derived, not set from an effect, so the picker never flashes.
  const selected = picked ?? (communities.length === 1 ? communities[0] : null);
  const onForm = Boolean(selected);

  // Every exit runs through here, so neither a picked community nor a failed post's message
  // survives into the next open: the card keeps this dialog mounted between opens.
  function reset() {
    setPicked(null);
    clearError();
  }

  function close() {
    reset();
    onClose();
  }

  function posted() {
    reset();
    (onPosted ?? onClose)();
  }

  const { submit, submitting, error, clearError } = useAskSubmit({
    kind,
    imageId,
    communityId: selected?.id ?? null,
    onPosted: posted,
  });

  const Form = FORMS[kind];
  const step = onForm ? 'form' : 'picker';

  return (
    <Dialog
      onOpenChange={(next) => {
        if (next || submitting) {
          return;
        }
        close();
      }}
      open={open}
    >
      {/*
        Escape: the flyout underneath is a Sheet from the same @artstorefronts/ui, and both
        resolve to the one @radix-ui/react-dismissable-layer in node_modules, so they share a
        layer stack and Radix dismisses only the topmost layer - one Escape closes this ask,
        never the flyout. The React-tree `onKeyDown` stop keeps the key from bubbling
        through the portal into the flyout's own handlers; Radix listens on the document,
        so it still sees it.
      */}
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          const launcher = returnFocusTo?.current;
          if (!launcher) {
            return;
          }
          event.preventDefault();
          launcher.focus();
        }}
        onEscapeKeyDown={(event) => {
          if (submitting) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (onForm || submitting) {
            event.preventDefault();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
          }
        }}
        showCloseButton={false}
      >
        <div className="flex items-start gap-2 px-4 pt-4 sm:px-6 sm:pt-6">
          {onForm && communities.length > 1 ? (
            <button
              aria-label={t('community-ask.back')}
              className={`-ml-2 ${ICON_BUTTON}`}
              disabled={submitting}
              onClick={() => setPicked(null)}
              type="button"
            >
              <ArrowLeft className="size-5" />
            </button>
          ) : null}
          {initialArtwork.thumb ? (
            <img
              alt={initialArtwork.title ?? ''}
              className="mt-1 size-12 shrink-0 rounded-md border border-gray-200 object-cover"
              src={initialArtwork.thumb}
            />
          ) : null}
          <div className="min-w-0 flex-1 pt-1">
            <DialogTitle className="text-base">{t(`community-ask.kinds.${kind}.${step}-title`)}</DialogTitle>
            <DialogDescription className="mt-1">
              {onForm
                ? t('community-ask.posting-to', { name: selected.name })
                : t(`community-ask.kinds.${kind}.picker-lede`)}
            </DialogDescription>
          </div>
          <button
            aria-label={t('community-ask.close')}
            className={`-mr-2 ${ICON_BUTTON}`}
            disabled={submitting}
            onClick={close}
            type="button"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-3 sm:px-6 sm:pt-4">
          {onForm ? (
            <div className="flex flex-col gap-4">
              <PieceRow artwork={initialArtwork} />
              <Form error={error} initialArtwork={initialArtwork} onSubmit={submit} submitting={submitting} />
            </div>
          ) : (
            <CommunityPicker communities={communities} loading={loading && !data} onSelect={setPicked} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
